import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

// How long the "Added to cart" confirmation stays on the button
const ADDED_STATE_DURATION = 2000;

// Liquid money values are always amount × 100, so they are formatted as a 2-decimal currency
const LIQUID_MONEY_BASIS = 'USD';

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price - Liquid money value (amount × 100)
 * @property {number} quantity - Minimum quantity allowed by the variant's quantity rule
 * @property {string | null} image
 */

/**
 * @typedef {object} PickerRefs
 * @property {HTMLElement[]} [items] - The product cards.
 * @property {HTMLElement} count - Selected pieces count.
 * @property {HTMLElement} total - Combined regular total.
 * @property {HTMLButtonElement} submitButton - Add to cart button.
 * @property {HTMLElement} submitLabel - Add to cart button label.
 * @property {HTMLElement} message - Inline validation / error message.
 * @property {HTMLElement} liveRegion - Polite live region.
 * @property {HTMLElement} [savings] - Informational discount message.
 * @property {HTMLElement} [savingsTier] - Tier line inside the savings message.
 * @property {HTMLElement} [savingsNote] - Note under the tier line.
 */

/**
 * The bundle detail page picker: selects products of one bundle and adds them to the cart as
 * separate lines at their regular prices. It never calculates or applies a discount; the discount
 * app does that at cart and checkout.
 *
 * @extends {Component<PickerRefs>}
 */
class RflctBundlePicker extends Component {
  requiredRefs = ['count', 'total', 'submitButton', 'submitLabel', 'message', 'liveRegion'];

  /** @type {WeakMap<HTMLElement, BundleVariant[]>} */
  #variantData = new WeakMap();

  /** @type {number | undefined} */
  #addedTimeout;

  #isAdding = false;

  connectedCallback() {
    super.connectedCallback();
    this.#updateSummary(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#addedTimeout);
  }

  get #items() {
    return this.refs.items ?? [];
  }

  get #selectedItems() {
    return this.#items.filter(
      (item) => item.dataset.selected === 'true' && item.dataset.soldOut !== 'true'
    );
  }

  /**
   * @param {HTMLElement} item
   * @returns {BundleVariant[]}
   */
  #variantsFor(item) {
    let variants = this.#variantData.get(item);

    if (!variants) {
      try {
        variants = JSON.parse(item.querySelector('script[data-variants]')?.textContent ?? '[]');
      } catch {
        variants = [];
      }
      this.#variantData.set(item, variants ?? []);
    }

    return variants ?? [];
  }

  /**
   * @param {HTMLElement} item
   * @returns {BundleVariant | undefined}
   */
  #selectedVariant(item) {
    const id = Number(item.dataset.variantId);
    return this.#variantsFor(item).find((variant) => variant.id === id);
  }

  /** @param {MouseEvent} event */
  toggle(event) {
    // The declarative event proxy points `target` at the element carrying `on:click`.
    const button = /** @type {HTMLButtonElement} */ (event.target);
    const item = /** @type {HTMLElement | null} */ (button.closest('.rflct-bundle-card'));
    if (!item || button.disabled) return;

    const selected = item.dataset.selected !== 'true';
    item.dataset.selected = String(selected);
    button.setAttribute('aria-pressed', String(selected));
    this.#hideMessage();
    this.#updateSummary();
  }

  selectAll() {
    for (const item of this.#items) {
      if (item.dataset.soldOut === 'true') continue;
      item.dataset.selected = 'true';
      item.querySelector('.rflct-bundle-card__toggle')?.setAttribute('aria-pressed', 'true');
    }
    this.#hideMessage();
    this.#updateSummary();
  }

  clearAll() {
    for (const item of this.#items) {
      item.dataset.selected = 'false';
      item.querySelector('.rflct-bundle-card__toggle')?.setAttribute('aria-pressed', 'false');
    }
    this.#updateSummary();
  }

  /** @param {Event} event */
  onVariantChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const item = /** @type {HTMLElement | null} */ (select.closest('.rflct-bundle-card'));
    if (!item) return;

    item.dataset.variantId = select.value;

    const variant = this.#selectedVariant(item);
    if (variant) {
      const price = item.querySelector('.rflct-bundle-card__price');
      if (price) price.textContent = this.#formatMoney(variant.price);

      const image = /** @type {HTMLImageElement | null} */ (item.querySelector('.rflct-bundle-card__image'));
      if (image && variant.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
      }
    }

    this.#hideMessage();
    this.#updateSummary();
  }

  async addSelected() {
    if (this.#isAdding) return;

    const selectedItems = this.#selectedItems;

    if (selectedItems.length === 0) {
      this.#showMessage(this.dataset.labelEmpty ?? '');
      return;
    }

    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }[]} */
    const lineItems = [];

    for (const item of selectedItems) {
      const variant = this.#selectedVariant(item);

      if (!variant?.available) {
        this.#showMessage((this.dataset.labelUnavailable ?? '').replace('[product]', item.dataset.productTitle ?? ''));
        /** @type {HTMLSelectElement | null} */ (item.querySelector('select'))?.focus();
        return;
      }

      /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
      const lineItem = { id: variant.id, quantity: variant.quantity || 1 };

      // Metadata for the discount app only; pricing never depends on it.
      if (this.dataset.bundleId) lineItem.properties = { _bundle: this.dataset.bundleId };

      lineItems.push(lineItem);
    }

    this.#hideMessage();
    this.#setAdding(true);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lineItems, sections: this.#cartSectionIds().join(',') }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        this.#showMessage(data?.description || data?.message || this.dataset.labelError || '');
        this.dispatchEvent(new CartErrorEvent(this.id, data?.message, data?.description, data?.errors));
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`)
        .then((res) => res.json())
        .catch(() => null);

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'rflct-bundle-picker',
          itemCount: lineItems.reduce((sum, lineItem) => sum + lineItem.quantity, 0),
          sections: data.sections,
        })
      );

      this.#showAddedState();
    } catch (error) {
      console.error(error);
      this.#showMessage(this.dataset.labelError ?? '');
    } finally {
      this.#setAdding(false);
    }
  }

  /** @returns {string[]} */
  #cartSectionIds() {
    const ids = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) ids.push(cartItems.dataset.sectionId);
    }
    return ids;
  }

  /** @param {boolean} [announce] */
  #updateSummary(announce = true) {
    const selectedItems = this.#selectedItems;
    const count = selectedItems.length;
    const totalCents = selectedItems.reduce((sum, item) => {
      const variant = this.#selectedVariant(item);
      return sum + (variant ? variant.price * (variant.quantity || 1) : 0);
    }, 0);

    const template = count === 1 ? this.dataset.labelCountOne : this.dataset.labelCountOther;
    this.refs.count.textContent = (template ?? '').replace('[count]', String(count));
    this.refs.total.textContent = this.#formatMoney(totalCents);
    this.refs.submitButton.disabled = count === 0;

    // Distinct products, never quantity: one card is one product.
    this.#updateSavings(new Set(selectedItems.map((item) => item.dataset.productId)).size);

    if (announce) {
      this.refs.liveRegion.textContent = `${this.refs.count.textContent}. ${this.refs.total.textContent}.`;
    }
  }

  /** @returns {{ pieces: number, percent: number }[]} */
  get #savingsTiers() {
    return (this.dataset.savingsTiers ?? '')
      .split(',')
      .map((pair) => {
        const [pieces, percent] = pair.split(':').map((part) => Number(part.trim()));
        return { pieces, percent };
      })
      .filter((tier) => tier.pieces > 0 && tier.percent > 0)
      .sort((a, b) => a.pieces - b.pieces);
  }

  /**
   * Shows which discount tier the selection has reached. Display only: no amount is ever
   * calculated and the total stays at regular prices.
   * @param {number} uniqueCount - Number of different products selected
   */
  #updateSavings(uniqueCount) {
    const { savings, savingsTier, savingsNote } = this.refs;
    if (!savings) return;

    const tiers = this.#savingsTiers;
    const reached = tiers.filter((tier) => uniqueCount >= tier.pieces).pop();
    const min = tiers[0]?.pieces ?? 0;
    const showPercent = this.dataset.savingsShowPercent !== 'false';

    let state = '';
    let text = '';
    let note = '';

    if (reached) {
      state = 'active';
      if (showPercent && this.dataset.labelSavingsPercent) {
        text = this.dataset.labelSavingsPercent.replace('[percent]', String(reached.percent));
        note = this.dataset.labelSavingsApplied ?? '';
      } else {
        text = this.dataset.labelSavingsActive ?? '';
      }
    } else if (uniqueCount > 0 && min > 0) {
      state = 'hint';
      text = (this.dataset.labelSavingsHint ?? '').replace('[min]', String(min));
    }

    savings.dataset.state = state;
    if (savingsTier) savingsTier.textContent = text;
    if (savingsNote) {
      savingsNote.textContent = note;
      savingsNote.hidden = note === '';
    }
    savings.hidden = text === '';
  }

  /** @param {number} cents - Price as Liquid outputs it (amount × 100) */
  #formatMoney(cents) {
    const currency = this.dataset.currency || '';
    const format = (this.dataset.moneyFormat || '{{amount}}').replace(/{{\s*currency\s*}}/g, currency);
    return formatMoney(cents, format, LIQUID_MONEY_BASIS);
  }

  /** @param {boolean} adding */
  #setAdding(adding) {
    this.#isAdding = adding;
    const { submitButton, submitLabel } = this.refs;

    submitButton.disabled = adding || this.#selectedItems.length === 0;
    submitButton.setAttribute('aria-busy', String(adding));

    if (adding) {
      submitLabel.dataset.label ??= submitLabel.textContent ?? '';
      submitLabel.textContent = this.dataset.labelAdding || submitLabel.dataset.label || '';
    } else if (!this.#addedTimeout) {
      submitLabel.textContent = submitLabel.dataset.label ?? submitLabel.textContent ?? '';
    }
  }

  #showAddedState() {
    const { submitLabel } = this.refs;
    submitLabel.dataset.label ??= submitLabel.textContent ?? '';
    submitLabel.textContent = this.dataset.labelAdded || submitLabel.dataset.label || '';

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => {
      submitLabel.textContent = submitLabel.dataset.label ?? '';
      this.#addedTimeout = undefined;
    }, ADDED_STATE_DURATION);
  }

  /** @param {string} message */
  #showMessage(message) {
    this.refs.message.textContent = message;
    this.refs.message.hidden = message === '';
  }

  #hideMessage() {
    this.refs.message.textContent = '';
    this.refs.message.hidden = true;
  }
}

if (!customElements.get('rflct-bundle-picker')) {
  customElements.define('rflct-bundle-picker', RflctBundlePicker);
}
